/**
 * useInterviewTypesManagement.ts
 *
 * V55.67: 면담 타입/템플릿 관리 훅
 *
 * @module shared/hooks/useInterviewTypesManagement
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useCurrentUser } from './useCurrentUser';

// ============================================
// 타입 정의
// ============================================

/** 면담 타입 */
export interface InterviewType {
  id: string;
  companyCode: string;
  code: string;
  name: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
  defaultTemplateId?: string; // V284: 대표 템플릿 ID
}

/** 면담 템플릿 */
export interface InterviewTemplate {
  id: string;
  companyCode: string;
  typeId: string;
  name: string;
  contentHtml: string;
  isActive: boolean;
  sortOrder: number;
}

// ============================================
// useInterviewTypes 훅
// ============================================

interface UseInterviewTypesResult {
  types: InterviewType[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createType: (data: CreateInterviewTypeData) => Promise<string>;
  updateType: (typeId: string, data: UpdateInterviewTypeData) => Promise<void>;
  deleteType: (typeId: string) => Promise<void>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

interface CreateInterviewTypeData {
  companyCode?: string; // V55.67.3: system_admin용
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
}

interface UpdateInterviewTypeData {
  name?: string;
  description?: string;
  isActive?: boolean;
  sortOrder?: number;
}

/**
 * 면담 타입 CRUD 훅
 *
 * @param options.companyCode V55.67.3: system_admin용 회사 코드 (없으면 BE에서 자동 결정)
 * @param options.includeInactive 비활성 타입 포함 여부
 * @param options.enabled V55.67.6: API 호출 활성화 여부 (기본값 true). false면 API 호출 안함.
 */
export function useInterviewTypes(options?: {
  companyCode?: string;
  includeInactive?: boolean;
  enabled?: boolean;
}): UseInterviewTypesResult {
  const { companyCode, includeInactive = false, enabled = true } = options ?? {};
  const { user } = useCurrentUser();

  const [types, setTypes] = useState<InterviewType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 목록 조회
  const fetchTypes = useCallback(async () => {
    // V55.67.6: enabled가 false면 API 호출 스킵
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    if (!user || !functions) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const listTypes = httpsCallable<
        { companyCode?: string; includeInactive?: boolean },
        { success: boolean; types: InterviewType[] }
      >(functions, 'listInterviewTypes');

      const payload = { companyCode, includeInactive };

      const result = await listTypes(payload);

      if (result.data.success) {
        setTypes(result.data.types);
      } else {
        throw new Error('면담 타입 조회 실패');
      }
    } catch (err: any) {
      console.error('[V55.67] useInterviewTypes fetch error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user, companyCode, includeInactive, enabled]);

  // 생성
  const createType = useCallback(async (data: CreateInterviewTypeData): Promise<string> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsCreating(true);

    try {
      const createFn = httpsCallable<
        CreateInterviewTypeData,
        { success: boolean; typeId: string }
      >(functions, 'createInterviewType');

      // V55.67.3: companyCode 주입 (hook options에서 전달받은 값 우선)
      const payload = companyCode ? { companyCode, ...data } : data;
      const result = await createFn(payload);

      if (result.data.success) {
        await fetchTypes(); // 목록 갱신
        return result.data.typeId;
      } else {
        throw new Error('면담 타입 생성 실패');
      }
    } catch (err: any) {
      console.error('[V55.67] createInterviewType error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsCreating(false);
    }
  }, [fetchTypes, companyCode]);

  // 수정
  const updateType = useCallback(async (typeId: string, data: UpdateInterviewTypeData): Promise<void> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsUpdating(true);

    try {
      const updateFn = httpsCallable<
        { companyCode?: string; typeId: string } & UpdateInterviewTypeData,
        { success: boolean }
      >(functions, 'updateInterviewType');

      // V55.67.3: companyCode 주입
      const payload = companyCode
        ? { companyCode, typeId, ...data }
        : { typeId, ...data };
      const result = await updateFn(payload);

      if (result.data.success) {
        await fetchTypes(); // 목록 갱신
      } else {
        throw new Error('면담 타입 수정 실패');
      }
    } catch (err: any) {
      console.error('[V55.67] updateInterviewType error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsUpdating(false);
    }
  }, [fetchTypes, companyCode]);

  // 삭제
  const deleteType = useCallback(async (typeId: string): Promise<void> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsDeleting(true);

    try {
      const deleteFn = httpsCallable<
        { companyCode?: string; typeId: string },
        { success: boolean; deletedTemplates: number }
      >(functions, 'deleteInterviewType');

      const payload = companyCode
        ? { companyCode, typeId }
        : { typeId };
      const result = await deleteFn(payload);

      if (result.data.success) {
        await fetchTypes(); // 목록 갱신
      } else {
        throw new Error('면담 타입 삭제 실패');
      }
    } catch (err: any) {
      console.error('[V73] deleteInterviewType error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsDeleting(false);
    }
  }, [fetchTypes, companyCode]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  return {
    types,
    isLoading,
    error,
    refetch: fetchTypes,
    createType,
    updateType,
    deleteType,
    isCreating,
    isUpdating,
    isDeleting,
  };
}

// ============================================
// useInterviewTemplates 훅
// ============================================

interface UseInterviewTemplatesResult {
  templates: InterviewTemplate[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createTemplate: (data: CreateInterviewTemplateData) => Promise<string>;
  updateTemplate: (templateId: string, data: UpdateInterviewTemplateData) => Promise<void>;
  deleteTemplate: (templateId: string) => Promise<void>;
  setDefaultTemplate: (typeId: string, templateId: string) => Promise<void>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSettingDefault: boolean;
}

interface CreateInterviewTemplateData {
  companyCode?: string; // V55.67.3: system_admin용
  typeId: string;
  name: string;
  contentHtml: string;
  sortOrder?: number;
}

interface UpdateInterviewTemplateData {
  name?: string;
  contentHtml?: string;
  isActive?: boolean;
  sortOrder?: number;
}

/**
 * 면담 템플릿 CRUD 훅
 *
 * @param options.companyCode V55.67.3: system_admin용 회사 코드
 * @param options.typeId 특정 타입의 템플릿만 조회 (optional)
 * @param options.includeInactive 비활성 템플릿 포함 여부
 * @param options.enabled V55.67.6: API 호출 활성화 여부 (기본값 true). false면 API 호출 안함.
 */
export function useInterviewTemplates(options?: {
  companyCode?: string;
  typeId?: string;
  includeInactive?: boolean;
  enabled?: boolean;
}): UseInterviewTemplatesResult {
  const { companyCode, typeId, includeInactive = false, enabled = true } = options ?? {};
  const { user } = useCurrentUser();

  const [templates, setTemplates] = useState<InterviewTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 목록 조회
  const fetchTemplates = useCallback(async () => {
    // V55.67.6: enabled가 false면 API 호출 스킵
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    if (!user || !functions) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const listTemplates = httpsCallable<
        { companyCode?: string; typeId?: string; includeInactive?: boolean },
        { success: boolean; templates: InterviewTemplate[] }
      >(functions, 'listInterviewTemplates');

      const payload = { companyCode, typeId, includeInactive };

      const result = await listTemplates(payload);

      if (result.data.success) {
        setTemplates(result.data.templates);
      } else {
        throw new Error('면담 템플릿 조회 실패');
      }
    } catch (err: any) {
      console.error('[V55.67] useInterviewTemplates fetch error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user, companyCode, typeId, includeInactive, enabled]);

  // 생성
  const createTemplate = useCallback(async (data: CreateInterviewTemplateData): Promise<string> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsCreating(true);

    try {
      const createFn = httpsCallable<
        CreateInterviewTemplateData,
        { success: boolean; templateId: string }
      >(functions, 'createInterviewTemplate');

      // V55.67.3: companyCode 주입
      const payload = companyCode ? { companyCode, ...data } : data;
      const result = await createFn(payload);

      if (result.data.success) {
        await fetchTemplates(); // 목록 갱신
        return result.data.templateId;
      } else {
        throw new Error('면담 템플릿 생성 실패');
      }
    } catch (err: any) {
      console.error('[V55.67] createInterviewTemplate error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsCreating(false);
    }
  }, [fetchTemplates, companyCode]);

  // 수정
  const updateTemplate = useCallback(async (templateId: string, data: UpdateInterviewTemplateData): Promise<void> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsUpdating(true);

    try {
      const updateFn = httpsCallable<
        { companyCode?: string; templateId: string } & UpdateInterviewTemplateData,
        { success: boolean }
      >(functions, 'updateInterviewTemplate');

      // V55.67.3: companyCode 주입
      const payload = companyCode
        ? { companyCode, templateId, ...data }
        : { templateId, ...data };
      const result = await updateFn(payload);

      if (result.data.success) {
        await fetchTemplates(); // 목록 갱신
      } else {
        throw new Error('면담 템플릿 수정 실패');
      }
    } catch (err: any) {
      console.error('[V55.67] updateInterviewTemplate error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsUpdating(false);
    }
  }, [fetchTemplates, companyCode]);

  // V262: 삭제
  const deleteTemplate = useCallback(async (templateId: string): Promise<void> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsDeleting(true);

    try {
      const deleteFn = httpsCallable<
        { companyCode?: string; templateId: string },
        { success: boolean }
      >(functions, 'deleteInterviewTemplate');

      const payload = companyCode
        ? { companyCode, templateId }
        : { templateId };
      const result = await deleteFn(payload);

      if (result.data.success) {
        await fetchTemplates(); // 목록 갱신
      } else {
        throw new Error('면담 템플릿 삭제 실패');
      }
    } catch (err: any) {
      console.error('[V262] deleteInterviewTemplate error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsDeleting(false);
    }
  }, [fetchTemplates, companyCode]);

  // V284: 대표 템플릿 설정
  const setDefaultTemplate = useCallback(async (typeId: string, templateId: string): Promise<void> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsSettingDefault(true);

    try {
      const fn = httpsCallable<
        { companyCode?: string; typeId: string; templateId: string },
        { success: boolean }
      >(functions, 'setDefaultInterviewTemplate');

      const payload = companyCode
        ? { companyCode, typeId, templateId }
        : { typeId, templateId };
      const result = await fn(payload);

      if (!result.data.success) {
        throw new Error('대표 템플릿 설정 실패');
      }
    } catch (err: any) {
      console.error('[V284] setDefaultInterviewTemplate error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsSettingDefault(false);
    }
  }, [companyCode]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    isLoading,
    error,
    refetch: fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setDefaultTemplate,
    isCreating,
    isUpdating,
    isDeleting,
    isSettingDefault,
  };
}

// ============================================
// 기존 InterviewType enum 호환용 상수
// ============================================

/**
 * 기존 하드코딩된 InterviewType 라벨
 * (마스터 데이터 마이그레이션 전 fallback으로 사용)
 */
export const LEGACY_INTERVIEW_TYPE_LABELS: Record<string, string> = {
  regular: '정기 면담',
  probation: '수습사원 면담',
  performance: '성과 면담',
  resignation: '퇴직 면담',
  return: '복직 면담',
  feedback: '피드백 면담',
  grievance: '고충 상담',
  other: '기타',
};

// ============================================
// V60.8: 조직별 면담 템플릿 선택 훅
// ============================================

/** 조직별 템플릿 선택 항목 */
export interface OrgTemplateSelection {
  typeCode: string;
  templateId: string;
  templateName: string;
}

interface UseOrgInterviewTemplateSelectionsResult {
  selections: OrgTemplateSelection[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  setSelection: (typeCode: string, templateId: string) => Promise<void>;
  isSetting: boolean;
}

/**
 * V60.8: 조직별 면담 템플릿 선택 관리 훅
 *
 * @param orgUnitId 조직 ID
 * @param companyCode system_admin용 회사 코드
 */
export function useOrgInterviewTemplateSelections(
  orgUnitId: string | undefined,
  companyCode?: string
): UseOrgInterviewTemplateSelectionsResult {
  const { user } = useCurrentUser();

  const [selections, setSelections] = useState<OrgTemplateSelection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSetting, setIsSetting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSelections = useCallback(async () => {
    if (!user || !functions || !orgUnitId) {
      setSelections([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fn = httpsCallable<
        { orgUnitId: string; companyCode?: string },
        { success: boolean; selections: OrgTemplateSelection[] }
      >(functions, 'getOrgInterviewTemplateSelections');

      const result = await fn({ orgUnitId, companyCode });
      if (result.data.success) {
        setSelections(result.data.selections);
      }
    } catch (err: any) {
      console.error('[V60.8] getOrgInterviewTemplateSelections error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user, orgUnitId, companyCode]);

  const setSelection = useCallback(async (typeCode: string, templateId: string) => {
    if (!functions || !orgUnitId) return;

    setIsSetting(true);

    try {
      const fn = httpsCallable<
        { orgUnitId: string; typeCode: string; templateId: string; companyCode?: string },
        { success: boolean; docId: string }
      >(functions, 'setOrgInterviewTemplate');

      const result = await fn({ orgUnitId, typeCode, templateId, companyCode });
      if (result.data.success) {
        await fetchSelections(); // 갱신
      }
    } catch (err: any) {
      console.error('[V60.8] setOrgInterviewTemplate error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsSetting(false);
    }
  }, [orgUnitId, companyCode, fetchSelections]);

  useEffect(() => {
    fetchSelections();
  }, [fetchSelections]);

  return {
    selections,
    isLoading,
    error,
    refetch: fetchSelections,
    setSelection,
    isSetting,
  };
}

/** V60.8: 면담 작성 시 조직 선택 템플릿 내용 조회 */
export interface OrgSelectedTemplateContent {
  templateId: string;
  templateName: string;
  contentHtml: string;
}

/**
 * V60.8: 조직의 특정 타입 선택 템플릿 내용 조회 함수
 * (hook이 아닌 일회성 호출 — InterviewWritePage에서 초기 로드 시 1회 호출)
 * V268: 대표 템플릿 소비 SSoT로 복원 (조직별 대표 → 전역 fallback 순서)
 */
export async function fetchOrgSelectedTemplateContent(
  orgUnitId: string,
  typeCode: string
): Promise<OrgSelectedTemplateContent | null> {
  if (!functions || !orgUnitId || !typeCode) return null;

  try {
    const fn = httpsCallable<
      { orgUnitId: string; typeCode: string },
      { success: boolean; template: OrgSelectedTemplateContent | null }
    >(functions, 'getOrgSelectedTemplateContent');

    const result = await fn({ orgUnitId, typeCode });
    return result.data.template;
  } catch (err) {
    console.error('[V60.8] fetchOrgSelectedTemplateContent error:', err);
    return null;
  }
}

/** V284: 대표 템플릿 내용 */
export interface DefaultTemplateContent {
  templateId: string;
  templateName: string;
  contentHtml: string;
}

/**
 * V284: 면담 타입의 대표 템플릿 내용 조회 함수
 * (hook이 아닌 일회성 호출 — InterviewWritePage에서 초기 로드 시 1회 호출)
 *
 * interviewTypes.defaultTemplateId 기반으로 템플릿 내용 조회.
 * orgInterviewTemplates 대체.
 *
 * @param params.typeId - 면담 타입 문서 ID (관리 페이지에서 사용)
 * @param params.typeCode - 면담 타입 코드 (InterviewWritePage에서 사용)
 */
export async function fetchDefaultTemplateContent(
  params: { typeId?: string; typeCode?: string }
): Promise<DefaultTemplateContent | null> {
  if (!functions || (!params.typeId && !params.typeCode)) return null;

  try {
    const fn = httpsCallable<
      { typeId?: string; typeCode?: string },
      { success: boolean; template: DefaultTemplateContent | null }
    >(functions, 'getDefaultTemplateContent');

    const result = await fn(params);
    return result.data.template;
  } catch (err) {
    console.error('[V284] fetchDefaultTemplateContent error:', err);
    return null;
  }
}
